import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PopoverPortal } from '@radix-ui/react-popover';

import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { cn } from 'src/utils';
import { generalMessages } from 'src/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { pricingOverridesMessages } from 'src/features/subscriptionItemPricingOverrides/utils/messages';
import { SubscriptionItemPricingOverrideFormSchemaValues } from 'src/features/subscriptionItemPricingOverrides/utils/formSchema';

type SubscriptionItemPricingOverrideFormProps = {
    form: UseFormReturn<SubscriptionItemPricingOverrideFormSchemaValues>;
    onSubmit: (values: SubscriptionItemPricingOverrideFormSchemaValues) => void;
    /** Creating while the item has an open coefficient: the new one closes it. */
    showAutoCloseHint?: boolean;
};

function SubscriptionItemPricingOverrideForm({
    onSubmit,
    form,
    showAutoCloseHint,
}: SubscriptionItemPricingOverrideFormProps) {
    const { formatMessage, formatDate } = useIntl();

    const { valid_from, valid_to } = form.watch();
    const [openValidFromCalendar, setOpenValidFromCalendar] = React.useState<boolean>(false);
    const [openValidToCalendar, setOpenValidToCalendar] = React.useState<boolean>(false);

    const earliestValidTo = new Date(valid_from);
    earliestValidTo.setHours(0, 0, 0, 0);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Popover open={openValidFromCalendar} onOpenChange={setOpenValidFromCalendar}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start text-base font-normal"
                                            onClick={() => setOpenValidFromCalendar(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={ICON_MAP.CALENDAR}
                                                className="text-primary-700 mr-1 size-4"
                                            />
                                            <span className={cn(!field.value && 'text-gray-500')}>
                                                {field.value
                                                    ? formatDate(new Date(field.value))
                                                    : `${formatMessage(generalMessages.selectDate)}...`}
                                            </span>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverPortal>
                                        <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                            <Calendar
                                                captionLayout="dropdown"
                                                mode="single"
                                                selected={field.value ? new Date(field.value) : undefined}
                                                onSelect={(date) => {
                                                    if (date) {
                                                        field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                        setOpenValidFromCalendar(false);
                                                    }
                                                }}
                                                disabled={
                                                    valid_to
                                                        ? { after: new Date(new Date(valid_to).setHours(0, 0, 0, 0)) }
                                                        : undefined
                                                }
                                            />
                                        </PopoverContent>
                                    </PopoverPortal>
                                </Popover>
                            </FormControl>
                            {showAutoCloseHint && (
                                <FormDescription>
                                    {formatMessage(pricingOverridesMessages.autoCloseHint)}
                                </FormDescription>
                            )}
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Popover open={openValidToCalendar} onOpenChange={setOpenValidToCalendar}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start text-base font-normal"
                                            onClick={() => setOpenValidToCalendar(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={ICON_MAP.CALENDAR}
                                                className="text-primary-700 mr-1 size-4"
                                            />
                                            <span className={cn(!field.value && 'text-gray-500')}>
                                                {field.value
                                                    ? formatDate(new Date(field.value))
                                                    : `${formatMessage(generalMessages.selectDate)}...`}
                                            </span>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverPortal>
                                        <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                            <Calendar
                                                captionLayout="dropdown"
                                                mode="single"
                                                selected={field.value ? new Date(field.value) : undefined}
                                                onSelect={(date) => {
                                                    if (date) {
                                                        field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                        setOpenValidToCalendar(false);
                                                    }
                                                }}
                                                disabled={
                                                    valid_from
                                                        ? {
                                                              before: new Date(
                                                                  earliestValidTo.getTime() + 24 * 60 * 60 * 1000,
                                                              ),
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </PopoverContent>
                                    </PopoverPortal>
                                </Popover>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="coefficient"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingOverridesMessages.coefficient)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                {/* A plain multiplier, not minor units and not a percentage —
                                    PriceFormField would divide it by 100. */}
                                <Input
                                    {...field}
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    value={field.value ?? ''}
                                    placeholder="1.0000"
                                />
                            </FormControl>
                            <p className="text-sm text-gray-500">
                                {formatMessage(pricingOverridesMessages.coefficientHint)}
                            </p>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default SubscriptionItemPricingOverrideForm;
